/* SPDX-FileCopyrightText: 2014-present Kriasoft */
/* SPDX-License-Identifier: MIT */

import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Chip,
  Divider,
  FormControl,
  IconButton,
  Modal,
  ModalDialog,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/joy";
import { useTranslation } from "react-i18next";
import {
  useLengthStore,
  type LengthPreference,
} from "../stores/lengthStore";
import { BetaBadge } from "./BetaBadge";

interface ResponseLengthModalProps {
  open: boolean;
  onClose: () => void;
}

const LENGTH_OPTIONS: LengthPreference[] = [
  "default",
  "short",
  "medium",
  "long",
];

export function ResponseLengthModal({
  open,
  onClose,
}: ResponseLengthModalProps): JSX.Element {
  const { t } = useTranslation();

  const selectedLength = useLengthStore((state) => state.selectedLength);
  const setSelectedLength = useLengthStore((state) => state.setSelectedLength);

  const handleLengthChange = (value: LengthPreference) => {
    setSelectedLength(value);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(2px)",
          },
        },
      }}
    >
      <ModalDialog
        sx={{
          width: { xs: "100vw", sm: "min(90vw, 500px)" },
          maxWidth: { xs: "100vw", sm: 500 },
          height: { xs: "100dvh", sm: "auto" },
          maxHeight: { xs: "100dvh", sm: "90vh" },
          m: { xs: 0, sm: 2 },
          px: 2.5,
          pt: { xs: "max(12px, env(safe-area-inset-top))", sm: 2.5 },
          pb: { xs: "max(12px, env(safe-area-inset-bottom))", sm: 2.5 },
          borderRadius: { xs: 0, sm: "md" },
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 1.5,
            }}
          >
            <Box
              sx={{ flex: 1, display: "flex", alignItems: "center", gap: 1 }}
            >
              <Typography level="title-lg" sx={{ fontSize: "1.1rem" }}>
                {t("lengthSettings.title")}
              </Typography>
              <BetaBadge />
            </Box>
            <IconButton onClick={onClose} size="sm" sx={{ p: 0.5 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Typography level="body-sm" sx={{ mb: 2, color: "text.secondary" }}>
            {t("lengthSettings.description")}
          </Typography>

          <Divider sx={{ my: 1 }} />

          <Box>
            <FormControl>
              <RadioGroup
                value={selectedLength}
                onChange={(e) =>
                  handleLengthChange(e.target.value as LengthPreference)
                }
                sx={{ gap: 1 }}
              >
                {LENGTH_OPTIONS.map((option) => (
                  <Radio
                    key={option}
                    value={option}
                    label={
                      <Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 0.5,
                          }}
                        >
                          <Typography level="title-sm">
                            {t(`lengthSettings.${option}`)}
                          </Typography>
                          {option === "default" && (
                            <Chip
                              size="sm"
                              variant="solid"
                              color="neutral"
                              sx={{
                                fontSize: "0.65rem",
                                fontWeight: 600,
                                px: 0.75,
                                py: 0.25,
                              }}
                            >
                              {t("lengthSettings.defaultBadge")}
                            </Chip>
                          )}
                        </Box>
                        <Typography
                          level="body-sm"
                          sx={{ color: "text.secondary" }}
                        >
                          {t(`lengthSettings.${option}Description`)}
                        </Typography>
                      </Box>
                    }
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Box>
        </Box>
      </ModalDialog>
    </Modal>
  );
}
